import type { RoomSnapshot } from '../../lib/api/rooms'
import { useT } from '../../i18n'

interface Props { snapshot: RoomSnapshot }

interface PlayerTally {
  playerId: string
  name: string
  yes: number
  no: number
  total: number
}

/**
 * Final leaderboard. Ranks players by total "I'd eat that" votes across
 * all three courses. Ties broken by fewer no-votes, then by join order
 * (earlier joiner wins — arbitrary but deterministic).
 */
export function TVResultsPhase({ snapshot }: Props) {
  const t = useT()
  const { players, submissions, courseVotes } = snapshot

  const tallies: PlayerTally[] = players.map(p => {
    const sub = submissions.find(s => s.player_id === p.id)
    const votesForPlayer = sub
      ? courseVotes.filter(cv => cv.submission_id === sub.id)
      : []
    const yes = votesForPlayer.filter(v => v.would_eat).length
    const no  = votesForPlayer.filter(v => !v.would_eat).length
    return { playerId: p.id, name: p.display_name, yes, no, total: yes - no }
  })

  tallies.sort((a, b) => {
    if (b.yes !== a.yes) return b.yes - a.yes
    if (a.no !== b.no)   return a.no - b.no
    return 0 // preserve join order (stable sort)
  })

  const winner = tallies[0]
  const podium = tallies.slice(0, 3)
  const others = tallies.slice(3)

  return (
    <div className="w-full max-w-5xl text-center">
      <p className="text-sm font-semibold uppercase tracking-widest text-brand-muted mb-3">{t('Final Leaderboard')}</p>
      <h1 className="font-display text-8xl md:text-9xl font-bold text-brand-text mb-4 leading-none">
        🏆 {winner?.name ?? t('No one?')} 🏆
      </h1>
      <p className="text-3xl text-brand-muted mb-12">
        {winner
          ? t("{n} 'I'd eat that' votes across 3 courses", { n: winner.yes })
          : t('The table was empty. Nobody to crown.')}
      </p>

      {/* Podium */}
      <div className="grid grid-cols-3 gap-4 mb-10 max-w-3xl mx-auto items-end">
        {podium.map((row, i) => {
          const heights = ['h-48', 'h-40', 'h-32']
          const medals = ['🥇', '🥈', '🥉']
          const colors = [
            'bg-iet-green/20 border-iet-green/40',
            'bg-brand-primary/15 border-brand-primary/40',
            'bg-brand-muted/15 border-brand-muted/40',
          ]
          return (
            <div key={row.playerId}
                 className={`card-lg ${heights[i]} ${colors[i]}
                             flex flex-col items-center justify-end p-4`}>
              <p className="text-5xl mb-2">{medals[i]}</p>
              <p className="font-display text-2xl font-bold truncate w-full">
                {row.name}
              </p>
              <p className="text-brand-muted text-lg">
                {t('{yes} yes · {no} no', { yes: row.yes, no: row.no })}
              </p>
            </div>
          )
        })}
        {/* Pad the podium if there were fewer than 3 players */}
        {Array.from({ length: Math.max(0, 3 - podium.length) }).map((_, i) => (
          <div key={`empty-${i}`} className="h-32" />
        ))}
      </div>

      {/* Others, if any */}
      {others.length > 0 && (
        <div className="mb-10">
          <p className="text-sm font-semibold uppercase tracking-widest text-brand-muted mb-3">{t('Also at the table')}</p>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {others.map(row => (
              <div key={row.playerId}
                   className="card p-4 flex items-center justify-between">
                <span className="font-semibold truncate">{row.name}</span>
                <span className="text-brand-muted">{row.yes} · {row.no}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <a href="/tv"
         className="inline-block px-8 py-4 rounded-2xl bg-brand-primary text-white
                    font-display text-2xl font-bold
                    shadow-lg active:scale-95 transition-all">{t('Watch Another Room')}</a>
    </div>
  )
}
